import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MachineIdentity from "@/components/maintenance/MachineMark";

// The module's small dialogs. Two shapes only:
//   ReasonDialog   — one required line ("what are we waiting for", "what's
//                    still wrong", "why was this a mistake").
//   RepairedDialog — the closure card: reason category (pre-filled from the
//                    report, editable) + what was done (required) + parts note.
//   PmServiceDialog — Phase 2: log a preventive service. Four tap chips, not
//                    checkboxes — gloved hands, one target each.
// Enter submits, Esc closes — both keyboard-first for gloved hands.

function useAutoFocus(open) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => ref.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);
  return ref;
}

export function ReasonDialog({
  open, onOpenChange, title, description, label, placeholder,
  confirmLabel, confirmTone = "orange", machine, onConfirm, busy, withSpare,
}) {
  const [text, setText] = useState("");
  // Phase 2 — the optional "log the spare order too?" block. Quiet, closed by
  // default, and never required: the pause stands on its own (§2 Slice 3).
  const [spareOpen, setSpareOpen] = useState(false);
  const [part, setPart] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const inputRef = useAutoFocus(open);
  useEffect(() => {
    if (!open) return;
    setText(""); setSpareOpen(false); setPart(""); setQty("1"); setPrice("");
  }, [open]);
  const ready = text.trim().length > 0;

  const submit = () => {
    if (!ready || busy) return;
    const spare = withSpare && spareOpen && part.trim()
      ? { part_name: part.trim(), qty: Number(qty) || 1, price_inr: price.trim() === "" ? null : Number(price) }
      : null;
    onConfirm(text.trim(), spare);
  };

  const tone = confirmTone === "green"
    ? "bg-green-600 hover:bg-green-500"
    : confirmTone === "slate"
      ? "bg-slate-600 hover:bg-slate-500"
      : "bg-orange-600 hover:bg-orange-500";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rl-frost" data-testid="mt-reason-dialog">
        <DialogHeader>
          <DialogTitle className="text-[17px]">{title}</DialogTitle>
          {description && <DialogDescription className="text-[12px]">{description}</DialogDescription>}
        </DialogHeader>
        {machine && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/25 px-3 py-2.5">
            <MachineIdentity machine={machine} size={26} />
          </div>
        )}
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))]">{label}</span>
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder={placeholder}
            data-testid="mt-reason-input"
            className="mt-1.5 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
          />
        </label>
        {withSpare && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 px-3 py-2.5">
            <button
              type="button"
              onClick={() => setSpareOpen((v) => !v)}
              data-testid="mt-pause-spare-toggle"
              className="flex items-center gap-2 text-[12px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            >
              <span className={`inline-block transition-transform ${spareOpen ? "rotate-90" : ""}`}>›</span>
              Log the spare order too? <span className="font-medium">(optional)</span>
            </button>
            {spareOpen && (
              <div className="mt-2.5 grid gap-2 sm:grid-cols-[1fr_70px_100px]">
                <input
                  value={part}
                  onChange={(e) => setPart(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
                  placeholder="Part ordered…"
                  data-testid="mt-pause-spare-part"
                  className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
                />
                <input
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  inputMode="decimal"
                  placeholder="Qty"
                  data-testid="mt-pause-spare-qty"
                  className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] font-mono-data text-right outline-none focus:border-[hsl(var(--primary))]/60"
                />
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  inputMode="decimal"
                  placeholder="₹ (optional)"
                  data-testid="mt-pause-spare-price"
                  className="h-9 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] font-mono-data text-right outline-none focus:border-[hsl(var(--primary))]/60"
                />
              </div>
            )}
            {spareOpen && (
              <p className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-2">
                It gets linked to this breakdown and shows on the Spares page until it arrives.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={() => onOpenChange(false)}
            className="rl-pill h-9 px-4 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!ready || busy}
            title={ready ? undefined : "Type a short reason first"}
            data-testid="mt-reason-confirm"
            className={`rl-lit-btn h-9 px-4 rounded-full text-white text-[12px] font-bold transition-colors disabled:opacity-45 disabled:cursor-not-allowed ${tone}`}
          >
            {busy ? "Saving…" : confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RepairedDialog({ open, onOpenChange, ticket, reasons, reasonOther, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [work, setWork] = useState("");
  const [parts, setParts] = useState("");
  const workRef = useAutoFocus(open);

  // Pre-fill the category from the report — maintenance usually confirms it
  // rather than re-picking, and can correct it here if production guessed wrong.
  const known = useMemo(() => (reasons || []).filter((r) => r !== reasonOther), [reasons, reasonOther]);
  useEffect(() => {
    if (!open || !ticket) return;
    const r = ticket.reason || "";
    const isKnown = known.includes(r);
    setReason(isKnown ? r : reasonOther);
    setCustom(isKnown ? "" : r);
    setWork(""); setParts("");
  }, [open, ticket, known, reasonOther]);

  const finalReason = reason === reasonOther ? custom.trim() : reason;
  const ready = work.trim().length > 0 && finalReason.length > 0;
  const submit = () => {
    if (!ready || busy) return;
    onConfirm({ reason: finalReason, root_note: work.trim(), parts_note: parts.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rl-frost" data-testid="mt-repaired-dialog">
        <DialogHeader>
          <DialogTitle className="text-[17px]">Repaired — hand over</DialogTitle>
          <DialogDescription className="text-[12px]">
            The machine waits in Handover until production confirms it runs fine.
          </DialogDescription>
        </DialogHeader>
        {ticket?.machine && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/25 px-3 py-2.5">
            <MachineIdentity machine={ticket.machine} size={26} />
          </div>
        )}

        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))]">What was the problem?</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="mt-repaired-reason"
            className="mt-1.5 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
          >
            {known.map((r) => <option key={r} value={r}>{r}</option>)}
            <option value={reasonOther}>{reasonOther}</option>
          </select>
          {reason === reasonOther && (
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Describe the problem in your words…"
              data-testid="mt-repaired-reason-custom"
              className="mt-2 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
            />
          )}
        </div>

        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))]">What did you do? <span className="text-[hsl(var(--primary))]">required</span></span>
          <textarea
            ref={workRef}
            value={work}
            onChange={(e) => setWork(e.target.value)}
            rows={3}
            placeholder="Replaced the ATC sensor and realigned the arm…"
            data-testid="mt-repaired-work"
            className="mt-1.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60 resize-none"
          />
        </label>

        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))]">Parts used <span className="font-medium normal-case tracking-normal">(optional)</span></span>
          <input
            value={parts}
            onChange={(e) => setParts(e.target.value)}
            placeholder="Seal kit, ATC proximity sensor…"
            data-testid="mt-repaired-parts"
            className="mt-1.5 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={() => onOpenChange(false)}
            className="rl-pill h-9 px-4 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!ready || busy}
            title={ready ? undefined : "Say what was done first"}
            data-testid="mt-repaired-confirm"
            className="rl-lit-btn h-9 px-4 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12px] font-bold transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
          >
            {busy ? "Saving…" : "Hand over to production"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function PmServiceDialog({ open, onOpenChange, machine, checklist, today, onConfirm, busy }) {
  const [date, setDate] = useState("");
  const [ticked, setTicked] = useState({});
  const [parts, setParts] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setDate(today || new Date().toISOString().slice(0, 10));
    setTicked({}); setParts(""); setNote("");
  }, [open, today]);

  const toggle = (k) => setTicked((t) => ({ ...t, [k]: !t[k] }));
  const submit = () => {
    if (busy || !date) return;
    onConfirm({ service_date: date, checklist: ticked, parts_note: parts.trim(), note: note.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rl-frost" data-testid="mt-pm-dialog">
        <DialogHeader>
          <DialogTitle className="text-[17px]">Log a service</DialogTitle>
          <DialogDescription className="text-[12px]">
            The next service schedules itself from this date and the machine's interval.
          </DialogDescription>
        </DialogHeader>
        {machine && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/25 px-3 py-2.5">
            <MachineIdentity machine={machine} size={26} />
          </div>
        )}

        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))]">Service date</span>
          <input
            type="date"
            value={date}
            max={today || undefined}
            onChange={(e) => setDate(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            data-testid="mt-pm-date"
            className="mt-1.5 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
          />
        </label>

        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))]">What was done? <span className="font-medium normal-case tracking-normal">(tap what applies)</span></span>
          <div className="flex flex-wrap gap-2 mt-2">
            {(checklist || []).map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => toggle(c.key)}
                data-testid={`mt-pm-chip-${c.key}`}
                className={`rl-prob-chip rl-pill ${ticked[c.key] ? "is-picked" : ""}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))]">Parts changed <span className="font-medium normal-case tracking-normal">(optional)</span></span>
          <input
            value={parts}
            onChange={(e) => setParts(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder="Spindle filter, way-lube cartridge…"
            data-testid="mt-pm-parts"
            className="mt-1.5 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
          />
        </label>

        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-[hsl(var(--muted-foreground))]">Note <span className="font-medium normal-case tracking-normal">(optional)</span></span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder="Coolant was low — topped up as well…"
            data-testid="mt-pm-note"
            className="mt-1.5 w-full h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={() => onOpenChange(false)}
            className="rl-pill h-9 px-4 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !date}
            data-testid="mt-pm-confirm"
            className="rl-lit-btn h-9 px-4 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12px] font-bold transition-colors disabled:opacity-45 disabled:cursor-not-allowed"
          >
            {busy ? "Saving…" : "Log PM service"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
